import { prettyJson, valOr,
  extendArray, isObject,
  valueOrFuncRes,
  AssertThrow,
} from '../SharedUtils.js'
import { ObjectVisitor } from './ObjectVisitor.js'

/*
Helper class that traverses an object hierarchy and collects all errors.
*/
export class ErrorCollector {
  constructor(parentObj, opts) {
    this.parentObj = parentObj;
    if (!this._implementsTraversal(parentObj)) {
      throw new Error(`ErrorCollector: parentObj must implement traversal: ${parentObj}`);
    }
    this.opts = valOr(opts, {});
  }

  _implementsTraversal(obj) {
    return isObject(obj) && obj.childObjs;
  }

  _getChildErrors(parentObj, errors, visited, data) {
    let ignoreSet = valOr(this.opts.ignoreSet, new Set());
    if (ignoreSet.has(parentObj)) {
      data.curDebugObj.result = "Ignored";
      return;
    }
    if (visited.has(parentObj)) {
      data.curDebugObj.result = "Already visited";
      return;
    }
    visited.add(parentObj);
    data.curDebugObj.name = data.curPathLabel;

    // Add errors from this object:
    if (parentObj.getInputPage) {
      //console.log(`Setting page: ${parentObj.getInputPage()}`)
      data.page = parentObj.getInputPage();
    }
    if (parentObj.getObjErrors) {
      // Add all of 'parentObj' errors to our list:
      let parentErrors = parentObj.getObjErrors().map((elem) => {
        let errorMsg = null;
        if (typeof elem === 'string') {
          errorMsg = elem;
        } else {
          errorMsg = elem.msg;
        }
        return {
          page: data.page,
          path: elem.path ? `${data.curPath}/${elem.path}` : data.curPath,
          pathLabel: data.curPathLabel,
          msg: errorMsg
        };
      });
      data.curDebugObj.errors = parentErrors;
      extendArray(errors, parentErrors);
    }

    // Iterate children and call recursively:
    if (parentObj.childObjs == '$none') {
      return;
    } else if (parentObj.childObjs == '$auto') {
      for (const key of Object.keys(parentObj)) {
        let childObj = parentObj[key];
        if (this._implementsTraversal(childObj) && childObj.enabled !== false) {
          let childPath = childObj.objInfo?.path ? valueOrFuncRes(childObj.objInfo.path) : key;
          let childName = childObj.objInfo?._name ? valueOrFuncRes(childObj.objInfo._name) : null;
          let newPath = `${data.curPath}/${childPath}`;
          let newPathLabel = childName ? `${data.curPathLabel ? data.curPathLabel + ' / ' : ''}${childName}`
            : data.curPathLabel;
          let newDebugObj = {};
          data.curDebugObj[key] = newDebugObj;
          this._getChildErrors(childObj, errors, visited, {
            ...data,
            curPath: newPath,
            curPathLabel: newPathLabel,
            curDebugObj: newDebugObj
          });
        } else if (Array.isArray(childObj)) {
          // Look in arrays for objects that have 'childObjs'
          // Note: if the array itself has the 'childObjs' property, prefix all child paths
          // with objInfo.path.
          let arrayBasePath = childObj.objInfo?.path ? childObj.objInfo?.path + '/' : '';
          data.curDebugObj[key] = [];
          for (let i = 0; i < childObj.length; ++i) {
            let elem = childObj[i];
            if (this._implementsTraversal(elem) && elem.enabled !== false) {
              // Get the child path id
              let newPath = null;
              if (valOr(elem.objInfo?.uniquePath, false)) {
                // The path in objInfo is unique (contains id or something), so use that as the full child path
                let childSubPath = arrayBasePath + valueOrFuncRes(elem.objInfo.path);
                newPath = `${data.curPath}/${childSubPath}`;
              } else {
                // The path in objInfo is not unique, so append the index to make it unique
                let childSubPath = arrayBasePath + (elem.objInfo?.path ? valueOrFuncRes(elem.objInfo.path) : key) + `#${i + 1}`;
                newPath = `${data.curPath}/${childSubPath}`;
              }
              // Get the child path label
              let childName = null;
              if (valOr(elem.objInfo?._uniqueName, false)) {
                // If the name is unique, just use that
                childName = valueOrFuncRes(elem.objInfo._name);
              } else {
                // If the name is not unique, append the index to make it unique
                let name = elem.objInfo?._name ? valueOrFuncRes(elem.objInfo._name) : key;
                childName = `${name} #${i + 1}`;
              }
              let newPathLabel = `${data.curPathLabel ? data.curPathLabel + ' / ' : ''}${childName}`;

              let newDebugObj = {};
              data.curDebugObj[key].push(newDebugObj);
              this._getChildErrors(elem, errors, visited, {
                ...data,
                curPath: newPath,
                curPathLabel: newPathLabel,
                curDebugObj: newDebugObj,
              });
            }
          }
        }
      }
    } else {
      throw new Error(`Unexpected value for 'childObjs': '${parentObj.childObjs}'`)
    }
  }

  _groupErrorsByPage(errors) {
    //console.log("ORIG: ", errors);
    let pageDict = {};
    for (const err of errors) {
      let page = err.page;
      if (!page) {
        page = {label: `${err.pathLabel}`, path: err.path};
      }
      if (!pageDict[page.path]) {
        pageDict[page.path] = {
          page: page,
          errors: [],
        };
      }
      pageDict[page.path].errors.push(err);
    }

    let pageErrors = [];
    for (const key in pageDict) {
      let elem = pageDict[key];
      //let errMsg = `Found ${elem.errors.length} error(s) here.`;
      let firstError = elem.errors[0];
      let firstErrorParts = firstError.pathLabel.split(' / ');
      let abbrevErrorPath = null;
      if (firstErrorParts.length > 1) {
        abbrevErrorPath = firstErrorParts.slice(1).join(' / ');
      } else {
        abbrevErrorPath = firstError.pathLabel;
      }
      let errMsg = `${abbrevErrorPath}: ${firstError.msg}`;
      if (elem.errors.length > 1) {
        errMsg += `..`
        //errMsg += ` <span class="AndMore">(+${elem.errors.length - 1} related)</span>`;
      }
      let childDetails = []
      for (const err of elem.errors) {
        let details = {
          path: err.path,
        }
        childDetails.push(details);
      }
      let error = {
        page: elem.page,
        path: elem.page.path,
        msg: errMsg,
        childDetails: childDetails,
      }
      pageErrors.push(error);
    }
    return pageErrors;
  }

  getErrors() {
    let errors = [];
    let debugDict = {};
    let data = {
      curPath: '',
      curPathLabel: '',
      page: null,
      debugDict: debugDict,
      curDebugObj: debugDict,
    }
    this._getChildErrors(this.parentObj, errors, new Set(), data);
    //console.log(`DebugDict: `, prettyJson(debugDict));
    let errorsByPage = this._groupErrorsByPage(errors);
    return errorsByPage;
  }
};